import type { FC } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import Button from '@atlaskit/button';
import type { PopupProps } from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary';
import { useLoomEntryPointErrorCallback } from '@confluence/loom-utils/entry-points/useLoomEntryPointErrorCallback';
import { useLoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariant';
import { LoomCrossJoinPopupCard } from '@confluence/loom-utils/entry-points/LoomCrossJoinPopupCard';
import { LoomCrossFlowPopupCard } from '@confluence/loom-utils/entry-points/LoomCrossFlowPopupCard';
import { DiscoverabilityPulse } from '@confluence/loom-utils/entry-points/DiscoverabilityPulse';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { withFlags } from '@confluence/flags/entry-points/withFlags';
import { expVal } from '@confluence/feature-experiments';

import { PageHeaderLoomButton } from './PageHeaderLoomButton';
import { LoomVideoIcon } from './VideoIcon';
import { LoomPageHeaderPlaceholder } from './LoomPageHeaderPlaceholder';

const ENTRYPOINT_ID = 'loom-confluence-page-header';
const SOURCE_COMPONENT = 'loomConfluencePageHeader';
const SOURCE_CONTEXT = 'confluencePageHeaderRecordAVideo';

const i18n = defineMessages({
	crossJoinEntryPointLabel: {
		id: 'page-header-loom-button.crossJoinEntryPointLabel',
		defaultMessage: 'Join your team on Loom to record a video',
		description:
			'The a11y-friendly ARIA label for the Loom button in the page header menu when a user is eligible for cross-join',
	},
	crossSellPopupCardDescription: {
		id: 'page-header-loom-button.crossSellPopupCardDescription',
		defaultMessage:
			'Save time and engage your team with a video walkthrough, demo, update, and more.',
		description: 'Description inside a popup card advertising an opportunity to try Loom',
	},
	crossSellPopupCardTitle: {
		id: 'page-header-loom-button.crossSellPopupCardTitle',
		defaultMessage: 'Share your work with a video',
		description: 'Title of a popup card advertising an opportunity to try Loom',
	},
	entryPointLabel: {
		id: 'page-header-loom-button.entryPointLabel',
		defaultMessage: 'Record a Loom video',
		description:
			'The a11y-friendly ARIA label and tooltip for the button in the page header to record a Loom video.',
	},
});

type PageHeaderLoomEntryPointComponentProps = {
	contentId: string;
	crossSellPopupPlacement?: PopupProps['placement'];
	spaceKey: string;
	contentType: string;
	flags: FlagsStateContainer;
	isCircle?: boolean;
	testId?: string;
	source?: string;
	isDisabled?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonSpacer = styled.div({
	minWidth: '30px',
});

const PageHeaderLoomEntryPointComponent: FC<PageHeaderLoomEntryPointComponentProps> = ({
	contentId,
	crossSellPopupPlacement,
	spaceKey,
	contentType,
	flags,
	isCircle = false,
	testId,
	source,
	isDisabled = false,
}) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	let entryPointVariant, error, loading;

	// if page is SSRd and the entry point variant was successfully preloaded during SSR
	// use the results from the window object
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if ((process.env.REACT_SSR || window.__SSR_RENDERED__) && window.__SSR_LOOM_ENTRY_POINT__) {
		entryPointVariant = window.__SSR_LOOM_ENTRY_POINT__.entryPointVariant;
		error = window.__SSR_LOOM_ENTRY_POINT__.error;
		loading = window.__SSR_LOOM_ENTRY_POINT__.loading;
	}

	// this hook will automatically return the preloaded entry point variant if it exists
	// however, this check will still occur for transitions/non-SSRd pages
	const entryPointFromState = useLoomEntryPointVariant();
	entryPointVariant = entryPointFromState.entryPointVariant;
	error = entryPointFromState.error;
	loading = entryPointFromState.loading;

	/**
	 * NONE being included here is to address a layout shift issue within the PageHeader while
	 * the entrypoint is loading. If we don't include NONE here, while a page is loading
	 * nothing will show where the entrypoint is, and then on load the entrypoint will pop in
	 * and shift the buttons next to it to the left.
	 */
	const isEntryPointEnabled = ['BETA', 'CO_USE', 'CROSS_FLOW', 'CROSS_JOIN', 'NONE'].includes(
		entryPointVariant,
	);

	const intl = useIntl();

	const loomEntryPointErrorCallback = useLoomEntryPointErrorCallback({ flags });

	if (!isEntryPointEnabled) {
		return null;
	}

	if (entryPointVariant === 'BETA' || entryPointVariant === 'CO_USE') {
		return (
			<PageHeaderLoomButton
				contentId={contentId}
				contentType={contentType}
				dataVC="loom-button"
				label={intl.formatMessage(i18n.entryPointLabel)}
				spaceKey={spaceKey}
				ssrPlaceholderIdProp={ssrPlaceholderIdProp}
				isCircle={isCircle}
				testId={testId}
				source={source}
				isDisabled={isDisabled && expVal('platform_editor_offline_editing_web', 'isEnabled', false)}
			/>
		);
	}

	if (error) {
		return (
			<Button
				appearance="subtle"
				aria-label={intl.formatMessage(i18n.entryPointLabel)}
				data-vc="loom-button"
				iconAfter={<LoomVideoIcon label="" color={token('color.icon.subtle')} />}
				onClick={loomEntryPointErrorCallback}
			/>
		);
	}

	if (loading) {
		return <LoomPageHeaderPlaceholder />;
	}

	if (entryPointVariant === 'CROSS_FLOW') {
		return (
			<LoomCrossFlowPopupCard
				description={intl.formatMessage(i18n.crossSellPopupCardDescription)}
				entryPointId={ENTRYPOINT_ID}
				placement={crossSellPopupPlacement}
				sourceActionSubjectId="createLoomHeaderItem"
				sourceComponent={SOURCE_COMPONENT}
				sourceContext={SOURCE_CONTEXT}
				title={intl.formatMessage(i18n.crossSellPopupCardTitle)}
				trigger={(triggerProps) => (
					<DiscoverabilityPulse entryPointId={ENTRYPOINT_ID} isCircle={isCircle}>
						<ButtonSpacer>
							<Button
								{...triggerProps}
								appearance="subtle"
								aria-label={intl.formatMessage(i18n.entryPointLabel)}
								data-vc="loom-button"
								iconAfter={<LoomVideoIcon label="" color={token('color.icon.subtle')} />}
								{...ssrPlaceholderIdProp}
							/>
						</ButtonSpacer>
					</DiscoverabilityPulse>
				)}
			/>
		);
	}

	if (entryPointVariant === 'CROSS_JOIN') {
		return (
			<LoomCrossJoinPopupCard
				description={intl.formatMessage(i18n.crossSellPopupCardDescription)}
				placement={crossSellPopupPlacement}
				sourceActionSubjectId="createLoomHeaderItem"
				title={intl.formatMessage(i18n.crossSellPopupCardTitle)}
				entryPointId={ENTRYPOINT_ID}
				triggerLink={(href, { onClick, ...otherTriggerProps }) => (
					<DiscoverabilityPulse entryPointId={ENTRYPOINT_ID} isCircle={isCircle}>
						<ButtonSpacer>
							<Button
								{...otherTriggerProps}
								appearance="subtle"
								aria-label={intl.formatMessage(i18n.crossJoinEntryPointLabel)}
								data-vc="loom-button"
								href={href}
								iconAfter={<LoomVideoIcon label="" color={token('color.icon.subtle')} />}
								rel="noreferrer"
								target="_blank"
								onClick={onClick}
								{...ssrPlaceholderIdProp}
							/>
						</ButtonSpacer>
					</DiscoverabilityPulse>
				)}
			/>
		);
	}

	// This condition currently isn't possible, but explicitly handling it here in case the
	// logic defining `isEntryPointEnabled` changes without updating this rendering behavior
	return null;
};

export const PageHeaderLoomEntryPoint = withErrorBoundary({
	attribution: Attribution.CONTENT_TYPES,
})(withFlags(PageHeaderLoomEntryPointComponent));
